import {
  Box,
  Modal,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import Iconify from "src/components/iconify";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { useTranslate } from "src/locales";

import { isFemale } from "src/utils/gender";

interface MenuModalProps {
  title: string;
  description: string;
  menus: ModalMenu[];
  open: boolean;
  onClose: () => void;
  patientFullName?: string;
  departmentName?: string;
  roomNumber?: string;
  age?: string;
  gender?: string;
}

interface ModalMenu {
  name: string;
  icon?: string;
  url: string;
  image?: string;
  clickFunction?: () => void;
  disabled?: boolean;
  tooltip?: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 430 },
  maxWidth: 430,
  bgcolor: "background.paper",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)",
  borderRadius: 3,
  overflow: "hidden",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  outline: "none",
  border: "1px solid",
  borderColor: "divider",
};

export const MenuModal = (props: MenuModalProps) => {
  const { t } = useTranslate();
  const visibleMenus = props.menus.filter((menu) => !menu.url?.startsWith("/nurse/patient-table/"));

  const avatarIcon = isFemale(props.gender)
    ? "/assets/icons/pages/nurse/patient-data/icons/user-woman-icon.svg"
    : "/assets/icons/pages/nurse/patient-data/icons/user.svg";

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <Box sx={style}>
        {/* Row 1: Title and Close Button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: "20px 28px",
            backgroundColor: "background.paper",
          }}
        >
          <ByzMuiTypography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "16px",
            }}
          >
            {t("menus.operations")}
          </ByzMuiTypography>
          <ByzMuiCloseButton onClick={props.onClose} />
        </Stack>

        {/* Row 2: Patient Info */}
        <Box
          sx={{
            padding: "20px 28px",
            backgroundColor: "rgba(224, 233, 245, 1)",
            borderBottom: "1px solid rgba(224, 233, 245, 1)",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src={avatarIcon}
              alt="Patient Avatar"
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            />
            <Box>
              <ByzMuiTypography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.5px",
                }}
              >
                {props.patientFullName}
              </ByzMuiTypography>
              <ByzMuiTypography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.5px",
                }}
              >
                {props.departmentName}
              </ByzMuiTypography>
              {props.roomNumber && (
                <ByzMuiTypography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {props.roomNumber}
                </ByzMuiTypography>
              )}
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Menu List */}
        <List
          sx={{
            padding: "16px 20px",
            overflowY: "auto",
            flex: 1,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "4px",
            },
          }}
        >
          {visibleMenus.map((menu, index) => {
            const isDisabled = menu.disabled === true;
            const tooltipText = menu.tooltip
              ? (typeof menu.tooltip === "string" ? t(menu.tooltip) : menu.tooltip)
              : (isDisabled ? t("under-development") : undefined);

            const listItem = (
              <ListItem
                key={`${menu.name}-${index}`}
                component={isDisabled ? "div" : (menu.url ? Link : "div")}
                to={isDisabled ? undefined : menu.url}
                sx={{
                  padding: "8px 16px",
                  marginBottom: "4px",
                  borderRadius: 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isDisabled ? "transparent" : "action.hover",
                  },
                  // Sabit renk - linke göre değişmeyecek
                  color: "text.primary",
                  textDecoration: "none",
                  "&:visited": {
                    color: "text.primary",
                  },
                  "&:link": {
                    color: "text.primary",
                  },
                }}
                onClick={isDisabled ? undefined : (!menu.url && menu.clickFunction ? menu.clickFunction : props.onClose)}
              >
                {menu.image ? (
                  <img
                    src={menu.image}
                    alt={menu.name}
                    style={{
                      marginRight: "12px",
                      width: "20px",
                      height: "20px",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  />
                ) : menu.icon ? (
                  <ListItemIcon sx={{ minWidth: 32, marginRight: "8px" }}>
                    <Iconify
                      icon={menu.icon}
                      sx={{
                        fontSize: 18,
                        color: isDisabled ? "text.disabled" : "primary.main"
                      }}
                    />
                  </ListItemIcon>
                ) : null}
                <ListItemText
                  primary={menu.name}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: isDisabled ? "text.disabled" : "text.primary",
                  }}
                />
              </ListItem>
            );

            return tooltipText ? (
              <Tooltip key={`${menu.name}-${index}`} title={tooltipText} arrow>
                <span style={{ display: "block" }}>{listItem}</span>
              </Tooltip>
            ) : (
              listItem
            );
          })}
        </List>
      </Box>
    </Modal>
  );
};
